import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../api/client.js'

export interface ChecklistTemplateItem {
  id: string
  slNo: number
  itemName: string
  description: string | null
}

export interface ChecklistAssignment {
  id: string
  conversationId: string
  checklistId: string
  checklistName: string
  checklistDescription: string | null
  assignedBy: string | null
  createdAt: string
  updatedAt: string
}

export interface ChecklistAssignmentItem {
  id: string
  assignmentId: string
  checklistItemId: string
  slNo: number
  itemName: string
  description: string | null
  isDone: boolean
  remarks: string | null
  createdAt: string
  updatedAt: string
}

export interface AvailableChecklist {
  id: string
  name: string
}

interface ChecklistResponse {
  assignment: ChecklistAssignment | null
  items: ChecklistAssignmentItem[]
}

interface AvailableChecklistsResponse {
  checklists: AvailableChecklist[]
}

interface ItemUpdateResponse {
  item: ChecklistAssignmentItem
}

interface AssignResponse {
  assignment: ChecklistAssignment
  items: ChecklistAssignmentItem[]
}

export function useConversationChecklistTab(conversationId: string | null) {
  const [assignment, setAssignment] = useState<ChecklistAssignment | null>(null)
  const [items, setItems] = useState<ChecklistAssignmentItem[]>([])
  const [available, setAvailable] = useState<AvailableChecklist[]>([])
  const [loading, setLoading] = useState(true)
  const [assigning, setAssigning] = useState(false)
  const [updatingItemId, setUpdatingItemId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const reqIdRef = useRef(0)

  const fetchChecklist = useCallback(async () => {
    if (!conversationId) return
    const reqId = ++reqIdRef.current
    setLoading(true)
    setError(null)
    try {
      const data = await api.get<ChecklistResponse>(`/conversations/${conversationId}/checklist`)
      if (reqId !== reqIdRef.current) return
      setAssignment(data.assignment)
      setItems(data.items)
    } catch (err) {
      if (reqId !== reqIdRef.current) return
      setError(err instanceof Error ? err.message : 'Failed to load checklist')
    } finally {
      if (reqId === reqIdRef.current) setLoading(false)
    }
  }, [conversationId])

  const fetchAvailable = useCallback(async () => {
    if (!conversationId) return
    try {
      const data = await api.get<AvailableChecklistsResponse>(`/conversations/${conversationId}/checklist/available`)
      setAvailable(data.checklists)
    } catch {
      // silent — dropdown will just be empty
    }
  }, [conversationId])

  useEffect(() => {
    void fetchChecklist()
    void fetchAvailable()
  }, [fetchChecklist, fetchAvailable])

  const assignChecklist = useCallback(
    async (checklistId: string): Promise<boolean> => {
      if (!conversationId) return false
      setAssigning(true)
      setError(null)
      try {
        const data = await api.post<AssignResponse>(`/conversations/${conversationId}/checklist/assign`, { checklistId })
        setAssignment(data.assignment)
        setItems(data.items)
        return true
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to assign checklist')
        return false
      } finally {
        setAssigning(false)
      }
    },
    [conversationId],
  )

  const updateItem = useCallback(
    async (itemId: string, patch: { isDone?: boolean; remarks?: string | null }): Promise<boolean> => {
      if (!conversationId) return false
      setUpdatingItemId(itemId)
      try {
        const data = await api.patch<ItemUpdateResponse>(`/conversations/${conversationId}/checklist/items/${itemId}`, patch)
        setItems((prev) => prev.map((it) => (it.id === itemId ? data.item : it)))
        return true
      } catch {
        return false
      } finally {
        setUpdatingItemId(null)
      }
    },
    [conversationId],
  )

  const toggleItemDone = useCallback(
    async (itemId: string, isDone: boolean) => {
      // Optimistic update
      setItems((prev) => prev.map((it) => (it.id === itemId ? { ...it, isDone } : it)))
      await updateItem(itemId, { isDone })
    },
    [updateItem],
  )

  const saveRemarks = useCallback(
    async (itemId: string, remarks: string | null, markDone?: boolean) => {
      const patch: { isDone?: boolean; remarks?: string | null } = { remarks }
      if (markDone) patch.isDone = true
      await updateItem(itemId, patch)
    },
    [updateItem],
  )

  const fetchTemplateItems = useCallback(
    async (checklistId: string): Promise<ChecklistTemplateItem[]> => {
      if (!conversationId) return []
      try {
        const data = await api.get<{ items: ChecklistTemplateItem[] }>(
          `/conversations/${conversationId}/checklist/templates/${checklistId}/items`,
        )
        return data.items ?? []
      } catch {
        return []
      }
    },
    [conversationId],
  )

  return {
    assignment,
    items,
    available,
    loading,
    assigning,
    updatingItemId,
    error,
    assignChecklist,
    toggleItemDone,
    saveRemarks,
    fetchTemplateItems,
    refresh: fetchChecklist,
  }
}
